/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import Link from "next/link";
import CarouselV3 from "@lululemon/ecom-common/src/components/carousel-v3";
import PropTypes from "prop-types";

import { Card, Heading, Image, constructSizes } from "@lululemon/ui-library";

import { ErrorBoundary } from "@lululemon/mwa-logger";
import { productEdHandleError } from "@lululemon/ecom-common/src/util/product-education-logger";
import classnames from "classnames";
import Reel from "@lululemon/ecom-common/src/components/reel/reel";
import {
  DEVICE_BREAKPOINTS,
  MAX_GRID_WIDTH,
} from "@lululemon/ecom-common/src/lib/breakpoints";
import { ABTestWrapper } from "@lululemon/mwa-ab-testing/esm";
import styles from "./card-carousel.module.scss";
import { CarouselDots } from "./carousel-dots";
import { useDeviceType } from "@lululemon/ecom-site/src/util/with-device-characteristics";
import { CardSubcontent } from "./card-subcontent";

const MEDIA_ASSET_WIDTH = 312;
const MEDIA_ASSET_HEIGHT = 401;
const MEDIA_ASSET_MAXWIDTH = MEDIA_ASSET_WIDTH * 2;

const CARD_ASSET_WIDTH = 650;
const CARD_ASSET_HEIGHT = 650;

export const TABLET_GRID_BREAKPOINT = `${DEVICE_BREAKPOINTS.tablet}px`;
export const DESKTOP_GRID_BREAKPOINT = `${DEVICE_BREAKPOINTS.desktop}px`;

const HEADING_ID = "card-carousel-hdg";

/* eslint-disable import/prefer-default-export */
export function CardCarousel({
  heading,
  cardCollection,
  isMobile,
  isTabletOrBelow,
}) {
  const [verticalCenterRef, setVerticalCenterRef] = React.useState(null);
  const { deviceType } = useDeviceType();

  const cardCarouselImgCallbackRef = React.useCallback((node) => {
    setVerticalCenterRef(node);
  }, []);

  return (
    <ErrorBoundary
      onError={(error) =>
        productEdHandleError(error, "CardCarousel", "CDP Card Carousel")
      }
    >
      <Heading
        className={classnames(styles.heading, "lll-text-small", "OneLinkTx")}
        tag="h1"
        id={HEADING_ID}
      >
        {heading}
      </Heading>
      <div className={classnames("row", styles.cardCarouselWrapper)}>
        <ABTestWrapper name="eps-carousel" abTestEndDate="06/01/2025">
          <CarouselV3
            ariaLabelledbyId={HEADING_ID}
            cellSpacing={
              deviceType.isMobile ? 8 : deviceType.isTablet ? 15 : 25
            }
            className={styles.slidesWrapper}
            disableEdgeSwiping={true}
            dragging={false}
            hideArrowsOnMobile={false}
            renderBottomCenterControls={isMobile && CarouselDots}
            scrollMode="remainder"
            slidesToShow={
              deviceType.isMobile ? 2.12 : deviceType.isTablet ? 3 : 4
            }
            wrapAround={false}
            verticalCenterRef={verticalCenterRef}
          >
            {cardCollection?.map((card, index) => {
              const {
                description,
                desktopMedia,
                image,
                imageDescription,
                imageUrl,
                mobileMedia,
                name,
                subContent,
                title,
                webRoute,
              } = card?.fields || {};
              const cardType = card?.sys?.contentType.sys.id;
              let cardContent;

              if (cardType === "featureReel") {
                cardContent = <Reel {...card?.fields} />;
              } else if (!isTabletOrBelow && cardType === "image") {
                cardContent = (
                  <Image
                    alt={
                      imageDescription != null
                        ? imageDescription
                        : "main carousel image"
                    }
                    className={classnames(
                      styles.cardImage,
                      styles.cardCarouselImageSlot
                    )}
                    height={MEDIA_ASSET_HEIGHT}
                    lazy={true}
                    maxWidth={MEDIA_ASSET_MAXWIDTH}
                    sizes={constructSizes([
                      {
                        breakpoint: MAX_GRID_WIDTH,
                        slotSize: "312px",
                      },
                      {
                        breakpoint: DESKTOP_GRID_BREAKPOINT,
                        slotSize: "22vw",
                      },
                      { slotSize: "0px" },
                    ])}
                    src={imageUrl}
                    width={MEDIA_ASSET_WIDTH}
                  />
                );
              } else if (
                cardType === "webPromoCard" ||
                cardType === "webComponentCardCarouselItem"
              ) {
                let cardImageUrl;
                let cardImageWidth;
                let cardSubContent;
                if (cardType === "webPromoCard") {
                  const cardImageFields =
                    isMobile && mobileMedia?.fields?.imageUrl
                      ? mobileMedia?.fields
                      : desktopMedia?.fields;
                  cardImageWidth = cardImageFields?.imageMeta?.width;
                  cardImageUrl = cardImageFields.imageUrl;
                  cardSubContent = description;
                } else {
                  cardImageUrl = isMobile
                    ? image?.fields?.mobileImageUrl
                    : image?.fields?.imageUrl;
                  cardSubContent = <CardSubcontent content={subContent} />;
                }
                cardContent = (
                  <Link href={webRoute || "/"} passHref>
                    <a className={styles.cardLink}>
                      <Card
                        aspectRatio="square"
                        classes={{
                          container: styles.cardContainer,
                          imageContainer: styles.cardImageContainer,
                          heading: styles.cardHeading,
                          subContent: classnames(
                            styles.cardSubContent,
                            "lll-text-body-3"
                          ),
                          textContainer: styles.cardTextContainer,
                        }}
                        heading={title}
                        headingLvl="h2"
                        imageProps={{
                          className: styles.cardImage,
                          height: CARD_ASSET_HEIGHT,
                          lazy: false,
                          maxWidth: cardImageWidth,
                          ref:
                            index === 1
                              ? cardCarouselImgCallbackRef
                              : undefined,
                          sizes: constructSizes([
                            {
                              breakpoint: MAX_GRID_WIDTH,
                              slotSize: "312px",
                            },
                            {
                              breakpoint: DESKTOP_GRID_BREAKPOINT,
                              slotSize: "22vw",
                            },
                            {
                              breakpoint: TABLET_GRID_BREAKPOINT,
                              slotSize: "22vw",
                            },
                            { slotSize: "43vw" },
                          ]),
                          src: cardImageUrl,
                          width: CARD_ASSET_WIDTH,
                        }}
                        subContent={cardSubContent}
                      />
                    </a>
                  </Link>
                );
              }

              return (
                !!cardContent && (
                  <div
                    aria-label={card?.header ? `${card?.header} Reel` : title}
                    aria-roledescription="slide"
                    className={styles.cardLink}
                    key={card?.header || name}
                    role="group"
                  >
                    {cardContent}
                  </div>
                )
              );
            })}
          </CarouselV3>
        </ABTestWrapper>
      </div>
    </ErrorBoundary>
  );
}

export const cardType = PropTypes.shape({
  name: PropTypes.string,
  aspectRatio: PropTypes.string,
  description: PropTypes.string,
  mobileMedia: PropTypes.shape({
    imageMeta: PropTypes.shape({
      height: PropTypes.number,
      width: PropTypes.number,
    }),
    imageUrl: PropTypes.string,
    name: PropTypes.string,
  }),
  title: PropTypes.string,
  webRoute: PropTypes.string,
});

CardCarousel.defaultProps = {
  heading: "",
  cardCollection: [],
  isMobile: false,
  isTabletOrBelow: false,
};

CardCarousel.propTypes = {
  heading: PropTypes.string,
  cardCollection: PropTypes.arrayOf(
    PropTypes.shape({
      fields: PropTypes.oneOfType([cardType]),
    })
  ),
  isMobile: PropTypes.bool,
  isTabletOrBelow: PropTypes.bool,
};
